import type { IActivity } from "@/api/Activities";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import Checkbox from "@/components/Checkbox/Checkbox";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { Text } from "@medoma/components/Text/Text";
import { useLingui } from "@lingui/react/macro";
import { useFormContext } from "react-hook-form";
import { activityRequirementStatusSchema } from "@medoma/models/activities";
import {
  Frequency,
  Times,
} from "../Activities/ActivityOccurrence/ActivitySchedule";
import styles from "./ActivitiesToClose.module.scss";

type ActivitiesToCloseFields = {
  activityIds: string[];
};

type ActivitiesToCloseProps = {
  activities: IActivity[];
  legend: string;
};

const CheckboxLabel = ({ activity }: { activity: IActivity }) => {
  const requiredCompetences =
    "requiredCompetences" in activity ? activity.requiredCompetences : [];
  return (
    <div className={styles.activity}>
      <CategoryIcon category={activity.category} size="small" />
      <Text element="span" size="small" weight="medium">
        {activity.title}
      </Text>
      <div className={styles.separator} />
      <Text element="span" size="small">
        <Frequency schedule={activity.schedule} />
      </Text>
      <div className={styles.separator} />
      <Text element="span" size="small">
        <Times schedule={activity.schedule} />
      </Text>
      {requiredCompetences.map((competence) => (
        <CompetenceChip
          key={competence}
          competence={competence}
          state={activityRequirementStatusSchema.enum.neutral}
          size="tiny"
        />
      ))}
    </div>
  );
};

const ActivityCheckbox = ({ activity }: { activity: IActivity }) => {
  const { register } = useFormContext<ActivitiesToCloseFields>();
  return (
    <Checkbox
      {...register("activityIds")}
      label={{
        text: activity.title,
        component: <CheckboxLabel activity={activity} />,
      }}
      value={activity.id}
      checkboxPosition="center"
    />
  );
};

export function ActivitiesToCloseOrReactivate({
  activities,
  legend,
}: ActivitiesToCloseProps) {
  const { t } = useLingui();
  const recurringActivities = activities.filter(({ recurring }) => recurring);
  const oneTimeActivities = activities.filter(({ recurring }) => !recurring);

  return (
    <div className={styles.activitiesSection}>
      <Text element="p" weight="medium">
        {legend}
      </Text>
      {recurringActivities.length > 0 ? (
        <Checkboxes legend={t`Återkommande aktiviteter`}>
          {recurringActivities.map((activity) => (
            <ActivityCheckbox key={activity.id} activity={activity} />
          ))}
        </Checkboxes>
      ) : null}
      {oneTimeActivities.length > 0 ? (
        <Checkboxes legend={t`Engångsaktiviteter`}>
          {oneTimeActivities.map((activity) => (
            <ActivityCheckbox key={activity.id} activity={activity} />
          ))}
        </Checkboxes>
      ) : null}
    </div>
  );
}
